import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { MatchHighlightsModal } from '../components/MatchHighlightsModal';
import { MatchSimModal } from '../components/MatchSimModal';
import { CompetitionAwards } from '../components/CompetitionAwards';
import { calculateStandings, getTeamOverall } from '../utils/gameData';
import { buildAsianCupTeams } from '../utils/realTeams';
import { updatePlayerStats, simulateKnockoutMatch } from '../utils/simulationEngine';
import { ArrowLeft, Trophy, Lightning, Globe, Lock, Eye, Play } from '@phosphor-icons/react';

const ROUND_ORDER = ['round32', 'round16', 'qf', 'sf', 'final'];
const ROUND_LABELS = { round32: 'Round of 32', round16: 'Round of 16', qf: 'Quarter-Finals', sf: 'Semi-Finals', final: 'FINAL' };

// We persist the Asian Cup under the legacy `championsLeague` / `championsLeagueTeams`
// keys so existing saves and engine code (updatePlayerStats competition='championsLeague')
// keep working untouched.
export const AsianCupPage = ({ gameState, onUpdateGameState }) => {
  const navigate = useNavigate();
  const teams = gameState?.teams || [];
  const currentWeek = gameState?.currentWeek || 1;
  const seasonComplete = currentWeek > 38;
  const asianTeams = gameState?.championsLeagueTeams || [];
  const [acState, setAcState] = useState(() => gameState?.championsLeague || null);
  const [highlightsMatch, setHighlightsMatch] = useState(null);
  const [simModalMatch, setSimModalMatch] = useState(null);

  const standings = calculateStandings(teams);
  const top4 = standings.slice(0, 4);

  const allAcTeams = useMemo(() => {
    const base = teams.filter(t => top4.some(q => q.id === t.id));
    return [...base, ...asianTeams];
  }, [teams, asianTeams, top4]);

  const getTeam = (id) => allAcTeams.find(t => t.id === id);

  const startAsianCup = () => {
    let bracketTeams = asianTeams;
    if (!bracketTeams || bracketTeams.length === 0) {
      bracketTeams = buildAsianCupTeams();
    }
    const combined = [...top4, ...bracketTeams];
    const shuffled = [...combined].sort(() => Math.random() - 0.5);
    const round32 = [];
    for (let i = 0; i < 16; i++) {
      round32.push({ homeId: shuffled[i * 2].id, awayId: shuffled[i * 2 + 1].id, homeScore: null, awayScore: null, played: false, winnerId: null, events: null, matchRatings: null });
    }
    const newAc = { round32, round16: [], qf: [], sf: [], final: [], phase: 'round32', complete: false };
    setAcState(newAc);
    onUpdateGameState({ ...gameState, championsLeague: newAc, championsLeagueTeams: bracketTeams });
  };

  const resetCup = () => {
    if (!window.confirm('Reset Asian Cup? All progress will be lost.')) return;
    setAcState(null);
    onUpdateGameState({ ...gameState, championsLeague: null });
  };

  const applyMatch = (ac, roundKey, tieIdx, home, away, result) => {
    const next = { ...ac, [roundKey]: [...ac[roundKey]] };
    const winnerId = result.pens
      ? (result.pens.winner === 'home' ? home.id : away.id)
      : (result.homeScore > result.awayScore ? home.id : result.awayScore > result.homeScore ? away.id : home.id);
    next[roundKey] = next[roundKey].map((m, i) => i === tieIdx
      ? { ...m, homeScore: result.homeScore, awayScore: result.awayScore, played: true, events: result.events, matchRatings: result.matchRatings, pens: result.pens || null, winnerId }
      : m);
    if (next[roundKey].every(m => m.played)) {
      const winners = next[roundKey].map(m => m.winnerId);
      const currentIdx = ROUND_ORDER.indexOf(roundKey);
      const nextKey = ROUND_ORDER[currentIdx + 1];
      if (nextKey && winners.length >= 2) {
        const nextMatches = [];
        for (let i = 0; i < winners.length; i += 2) {
          nextMatches.push({ homeId: winners[i], awayId: winners[i + 1], homeScore: null, awayScore: null, played: false, winnerId: null, events: null, matchRatings: null });
        }
        next[nextKey] = nextMatches;
        next.phase = nextKey;
      } else if (roundKey === 'final') {
        next.complete = true;
        next.phase = 'complete';
      }
    }
    return next;
  };

  const persistStatsUpdate = (homeTeam, awayTeam, result, newAc) => {
    const ewflUpdated = updatePlayerStats(teams, result.events, homeTeam.name, awayTeam.name, result.homeScore, result.awayScore, 'championsLeague');
    const acUpdated = updatePlayerStats(asianTeams, result.events, homeTeam.name, awayTeam.name, result.homeScore, result.awayScore, 'championsLeague');
    let trophyHistory = gameState.trophyHistory || [];
    if (newAc.complete && newAc.final?.[0]?.winnerId) {
      const champion = allAcTeams.find(t => t.id === newAc.final[0].winnerId);
      const alreadyRecorded = trophyHistory.some(h => h.season === gameState.season && h.competition === 'asianCup');
      if (!alreadyRecorded && champion) {
        trophyHistory = [...trophyHistory, { season: gameState.season, year: gameState.year || 2026, competition: 'asianCup', competitionLabel: 'Asian Cup', winnerId: champion.id, winnerName: champion.name }];
      }
    }
    setAcState(newAc);
    onUpdateGameState({ ...gameState, teams: ewflUpdated, championsLeagueTeams: acUpdated, championsLeague: newAc, trophyHistory });
  };

  const simOne = (roundKey, tieIdx) => {
    const m = acState[roundKey][tieIdx];
    if (m.played) return;
    const home = getTeam(m.homeId);
    const away = getTeam(m.awayId);
    if (!home || !away) return;
    const result = simulateKnockoutMatch(home, away, 0, 0);
    const newAc = applyMatch(acState, roundKey, tieIdx, home, away, result);
    persistStatsUpdate(home, away, result, newAc);
  };

  const openFullSim = (roundKey, tieIdx) => {
    const m = acState[roundKey][tieIdx];
    if (m.played) return;
    const home = getTeam(m.homeId);
    const away = getTeam(m.awayId);
    if (!home || !away) return;
    setSimModalMatch({ home, away, onApply: (result) => {
      const newAc = applyMatch(acState, roundKey, tieIdx, home, away, result);
      persistStatsUpdate(home, away, result, newAc);
    }});
  };

  const BracketMatch = ({ match, idx, roundKey }) => {
    const home = getTeam(match.homeId);
    const away = getTeam(match.awayId);
    if (!home || !away) return null;
    const homeIsJWFL = teams.some(t => t.id === home.id);
    const awayIsJWFL = teams.some(t => t.id === away.id);
    return (
      <div className="card-elevated rounded-sm p-3" data-testid={`ac-match-${roundKey}-${idx}`}>
        <div className="flex items-center gap-2">
          <span className={`text-sm flex-1 truncate ${match.winnerId === home.id ? 'text-[#CCFF00] font-bold' : 'text-white'}`}>
            {homeIsJWFL ? '🇯🇵 ' : (home.flag || '') + ' '}{home.name}
          </span>
          {match.played && <span className="font-mono text-sm font-bold text-white">{match.homeScore}</span>}
        </div>
        <div className="flex items-center gap-2 mt-1">
          <span className={`text-sm flex-1 truncate ${match.winnerId === away.id ? 'text-[#CCFF00] font-bold' : 'text-white'}`}>
            {awayIsJWFL ? '🇯🇵 ' : (away.flag || '') + ' '}{away.name}
          </span>
          {match.played && <span className="font-mono text-sm font-bold text-white">{match.awayScore}</span>}
        </div>
        {match.winnerId && <p className="text-[10px] text-[#CCFF00] mt-1">OVR: {getTeamOverall(getTeam(match.winnerId))}</p>}
        <div className="flex gap-1 mt-2 flex-wrap">
          {!match.played ? (
            <>
              <Button size="sm" onClick={() => simOne(roundKey, idx)} className="bg-[#FF006E] hover:bg-[#FF3D8E] text-white text-[10px] h-6 px-2" data-testid={`sim-ac-${roundKey}-${idx}`}>
                <Lightning size={10} className="mr-1" /> Quick
              </Button>
              <Button size="sm" onClick={() => openFullSim(roundKey, idx)} className="bg-[#00E5FF] hover:bg-[#66F2FF] text-black text-[10px] h-6 px-2" data-testid={`full-ac-${roundKey}-${idx}`}>
                <Play size={10} className="mr-1" /> Full
              </Button>
            </>
          ) : (
            <Button size="sm" variant="outline" onClick={() => setHighlightsMatch({ match: { ...match, homeTeamName: home.name, awayTeamName: away.name }, homeTeam: home, awayTeam: away })} className="text-[10px] h-6 px-2 bg-transparent border-[#1E2240] text-white" data-testid={`hl-ac-${roundKey}-${idx}`}>
              <Eye size={10} className="mr-1" /> Stats
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#04060F] pt-20 pb-8 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <Button onClick={() => navigate('/')} variant="ghost" className="text-[#7A86B5] hover:text-white" data-testid="ac-back-btn"><ArrowLeft size={20} /></Button>
          <div className="flex-1">
            <h1 className="font-heading font-black text-3xl sm:text-4xl text-white uppercase tracking-wide flex items-center gap-3">
              <Globe size={32} className="text-[#CCFF00]" weight="fill" /> Asian Cup
            </h1>
            <p className="text-[#7A86B5] mt-1">Top 4 JWFL clubs vs 28 of Asia's best · 32-team knockout</p>
          </div>
          {acState && (
            <Button variant="outline" onClick={resetCup} className="bg-transparent border-[#FF006E] text-[#FF006E]" data-testid="reset-ac-btn">Reset</Button>
          )}
        </div>

        {!seasonComplete && !acState ? (
          <div className="card-surface rounded-sm p-12 text-center">
            <Lock size={64} className="text-[#4A5278] mx-auto mb-4" />
            <h2 className="font-heading font-bold text-2xl text-white uppercase mb-2">Season Not Complete</h2>
            <p className="text-[#7A86B5] mb-4">Complete the JWFL J.League season (Week 38) to unlock the Asian Cup.</p>
            <p className="font-mono text-sm text-[#FF006E] mt-4">Current: Week {currentWeek}/38</p>
          </div>
        ) : !acState ? (
          <div className="card-surface rounded-sm p-8 text-center">
            <Globe size={64} weight="fill" className="text-[#CCFF00] mx-auto mb-4" />
            <h2 className="font-heading font-bold text-2xl text-white uppercase mb-4">JWFL Qualifiers · Top 4</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-2xl mx-auto mb-6">
              {top4.map((team, idx) => (
                <div key={team.id} className="card-elevated rounded-sm p-3 text-center">
                  <p className="font-mono text-lg font-bold text-[#CCFF00]">#{idx + 1}</p>
                  <p className="text-sm font-medium text-white mt-1">{team.name}</p>
                  <p className="text-xs text-[#4A5278]">OVR: {getTeamOverall(team)}</p>
                  <p className="text-xs text-[#7A86B5]">{team.stats.points} pts</p>
                </div>
              ))}
            </div>
            <p className="text-[#7A86B5] text-sm mb-4">These 4 teams will face 28 elite Asian clubs from K-League, CSL, SPL, A-League, ISL, and more.</p>
            <Button data-testid="start-asian-cup-btn" onClick={startAsianCup} className="bg-[#CCFF00] hover:bg-[#A8D900] text-black font-bold px-8">
              <Trophy size={18} className="mr-2" weight="fill" /> Draw Bracket & Start
            </Button>
          </div>
        ) : (
          <Tabs defaultValue="bracket" className="w-full">
            <TabsList className="bg-[#0A0D1F] border border-[#1E2240] w-full grid grid-cols-2 mb-6">
              <TabsTrigger value="bracket" className="data-[state=active]:bg-[#CCFF00] data-[state=active]:text-black font-heading uppercase text-sm" data-testid="ac-tab-bracket">Bracket</TabsTrigger>
              <TabsTrigger value="awards" className="data-[state=active]:bg-[#FF006E] data-[state=active]:text-white font-heading uppercase text-sm" data-testid="ac-tab-awards">Awards</TabsTrigger>
            </TabsList>

            <TabsContent value="bracket">
              <div className="space-y-8">
                {ROUND_ORDER.map(roundKey => {
                  const round = acState[roundKey];
                  if (!round || round.length === 0) return null;
                  const cols = roundKey === 'final' ? 'grid-cols-1 max-w-md mx-auto' : roundKey === 'sf' ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4';
                  return (
                    <div key={roundKey}>
                      <h2 className="font-heading font-bold text-xl text-white uppercase mb-3">{ROUND_LABELS[roundKey]}</h2>
                      <div className={`grid gap-3 ${cols}`}>
                        {round.map((match, idx) => <BracketMatch key={idx} match={match} idx={idx} roundKey={roundKey} />)}
                      </div>
                    </div>
                  );
                })}
                {acState.complete && acState.final?.[0]?.winnerId && (
                  <div className="card-surface rounded-sm p-8 text-center">
                    <Globe size={64} weight="fill" className="text-[#CCFF00] mx-auto mb-3" />
                    <h2 className="font-heading font-black text-3xl text-[#CCFF00] uppercase">Asian Cup Champions!</h2>
                    <p className="text-white text-xl mt-2">{getTeam(acState.final[0].winnerId)?.flag} {getTeam(acState.final[0].winnerId)?.name}</p>
                  </div>
                )}
              </div>
            </TabsContent>

            <TabsContent value="awards">
              <CompetitionAwards teams={allAcTeams} competition="championsLeague" topN={25} />
            </TabsContent>
          </Tabs>
        )}
      </div>

      <MatchHighlightsModal
        isOpen={!!highlightsMatch}
        onClose={() => setHighlightsMatch(null)}
        match={highlightsMatch?.match}
        homeTeam={highlightsMatch?.homeTeam}
        awayTeam={highlightsMatch?.awayTeam}
        competitionLabel="Asian Cup"
      />

      <MatchSimModal
        isOpen={!!simModalMatch}
        onClose={() => setSimModalMatch(null)}
        homeTeam={simModalMatch?.home}
        awayTeam={simModalMatch?.away}
        competitionLabel="Asian Cup"
        knockout={true}
        onResult={(result) => { simModalMatch?.onApply?.(result); }}
      />
    </div>
  );
};

export default AsianCupPage;
